<template>
  <div>
    <div class="mb-4 flex w-full flex-wrap items-center justify-between gap-2">
      <BaseSegmentedControl
        v-model="localFilters.status"
        :options="statusFilterOptions"
      />
      <BaseDateIntervalSelect
        :model-value="dateRange"
        @update:model-value="emit('update:dateRange', $event)"
      />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseInputSearch v-model="localFilters.search" />
      <BasePagination v-model="page" :page-count="pageCount" class="ml-auto" />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseCountSelect
        v-model="paginationCount"
        :total-count="data?.count || 0"
      />
      <button
        type="button"
        class="text-primary border-1 border-primary relative rounded-lg px-2 py-1"
        :disable="isFetching"
        @click="() => refetch()"
      >
        refresh
        <template v-if="isFetching">
          <BaseContainerLoading spinner-class="h-4 w-4 " />
        </template>
      </button>
    </div>
    <BaseTable
      key-field="id"
      :rows="rows"
      :columns="columns"
      :row-class="(row) => (row.onDisallowList ? 'bg-red bg-opacity-10' : '')"
      :is-loading="isFetching"
    >
      <template #body-cell:status="{ value }">
        <CameraEventStatusChip :status="value" />
      </template>

      <template #body-cell:eventTimestamp="{ value }">
        <BaseTableDatetimeCell :value="value" />
      </template>

      <template #body-cell:vehicleRegistration="{ value }">
        <p class="text-md font-bold uppercase">
          {{ value }}
        </p>
      </template>

      <template #body-cell:image="{ value }">
        <a
          v-if="value"
          :href="value"
          target="_blank"
          rel="noopener"
          title="ANPR overview image"
        >
          <img
            :src="value"
            alt="ANPR overview"
            class="h-12 w-auto rounded border border-gray-200 object-cover"
            loading="lazy"
          />
        </a>
        <span v-else class="text-gray-400">—</span>
      </template>
    </BaseTable>
  </div>
</template>

<script setup lang="ts">
import {
  type CameraEvent,
  CameraEventStatusOptions,
} from "@/services/cameraEvents/listCameraEvents";
import { useSiteColumn } from "@/services/locations/useSiteColumn";
import { type ColumnDefinition } from "@/types/table";

type DateInterval = {
  start?: Date;
  end?: Date;
};

// `filters` scopes the table to a record; the visible date range arrives as
// v-model:date-range so the caller decides who owns it. The Entry Log page binds
// the shared model (useDatePickerContext) to keep one range across its ANPR and
// Keypad tabs, while the booking and allowlist detail views each keep their own —
// a booking's entry log in particular must stay unbounded rather than inherit
// whatever range someone last picked on the Entry Log page. Don't mirror the
// range into localFilters: that's spread into the query after it, so a local copy
// shadows the bound one and the picker silently stops driving the query.
type CameraEventsTableFilters = {
  bookingId?: string;
  vehicleRegistration?: string;
};

const props = withDefaults(
  defineProps<{
    dateRange: DateInterval;
    filters?: CameraEventsTableFilters;
  }>(),
  {
    filters: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "update:dateRange", value: DateInterval): void;
}>();

const { siteColumns } = useSiteColumn<CameraEvent>((row) => row.siteId);

const columns = computed<ColumnDefinition<CameraEvent>[]>(() => [
  {
    label: "Timestamp",
    name: "eventTimestamp",
    getValue: (row) => row.eventDatetime,
    autoWidth: true,
  },
  {
    label: "Vehicle Registration",
    name: "vehicleRegistration",
    getValue: (row) => row.vehicleRegistration,
    autoWidth: true,
  },
  ...siteColumns.value,
  {
    label: "Guest Name",
    name: "guestName",
    getValue: (row) => row.guestName || "-",
    autoWidth: true,
  },
  {
    label: "Pitch/Unit",
    name: "pitchInfo",
    getValue: (row) => row.pitchInfo || "-",
    autoWidth: true,
  },
  {
    label: "Status",
    name: "status",
    getValue: (row) => row.status,
    autoWidth: true,
  },
  {
    label: "Reason",
    name: "reason",
    getValue: (row) => row.reasonLabel,
    autoWidth: true,
  },
  {
    label: "Camera",
    name: "cameraName",
    getValue: (row) => row.cameraName,
    autoWidth: true,
  },
  {
    label: "Image",
    name: "image",
    getValue: (row) => row.overviewImage ?? "",
    autoWidth: true,
  },
]);

interface LocalFilters {
  status: "authorized" | "unknown" | "denied" | "ignored" | null;
  search: string;
}

const localFilters = reactive<LocalFilters>({
  status: null,
  search: "",
});

const paginationCount = ref(100);

const { pagination, page, pageCount, resetPagination } = usePagination({
  limit: paginationCount,
  count: computed(() => data.value?.count || 0),
});

watch(
  () => [props.filters, props.dateRange, localFilters],
  () => resetPagination(),
  { deep: true, immediate: true },
);

const query = reactiveComputed(() => ({
  ...props.filters,
  ...localFilters,
  fromDt: props.dateRange.start,
  untilDt: props.dateRange.end,
  ...pagination.value,
}));

const { data, isFetching, refetch } = useCameraEvents(query);

const rows = computed<Array<CameraEvent>>(() => data.value?.items ?? []);

const statusFilterOptions = [
  { label: "All", value: null },
  ...CameraEventStatusOptions,
];
</script>
